import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useDebounceValue } from "usehooks-ts";

import { hideShipmentLane } from "../../../../../containers/ShipmentLane/actions";
import { parseLocation } from "../utils";

/**
 * Handles the state and logic for the city and postal code fields.
 * The `query` represents the user input in the dropdown filter.
 * The `value` represents the selected city and postal code as a JSON string.
 *
 * @param {{ cityName: string, hasPostalCode: boolean, postalCodeName: string, onLocationSelect: Function }} props
 * @returns {{ countryCode: string, city: string, onChange: Function, onFilter: Function, postalCode: string, query: string, value: string }}
 */
export const useCityAndPostalCode = ({
  cityName,
  countryCode,
  hasPostalCode,
  postalCodeName,
  onLocationSelect,
  cityValue,
  postalCodeValue,
}) => {
  const [query, setQuery] = useDebounceValue("", 300);
  const [value, setValue] = useState("{}");
  const countryCodeRef = useRef(countryCode);
  const dispatch = useDispatch();

  useEffect(() => {
    if (countryCodeRef.current !== countryCode) {
      setQuery("");
      setValue("{}");
      countryCodeRef.current = countryCode;
    }
  }, [countryCode, setQuery]);

  // Set initial city and postal code based on props
  useEffect(() => {
    if (cityValue && postalCodeValue) {
      const json = JSON.stringify({
        city: cityValue,
        postalCode: postalCodeValue,
      });
      setValue(json);
    }
  }, [cityValue, postalCodeValue]);

  const onChange = useCallback(
    ({ detail: { value: changeValue } }) => {
      const { city, postalCode, json } = parseLocation(changeValue);
      setValue(json);

      onLocationSelect({ name: cityName, value: city });

      if (hasPostalCode) {
        onLocationSelect({ name: postalCodeName, value: postalCode });
      }

      dispatch(hideShipmentLane());
    },
    [cityName, dispatch, hasPostalCode, onLocationSelect, postalCodeName]
  );

  const onFilter = useCallback(
    ({ detail: filterValue }) => {
      if (filterValue !== null) setQuery(filterValue);
    },
    [setQuery]
  );

  return useMemo(() => {
    const { city, postalCode } = parseLocation(value);

    return {
      city,
      onChange,
      onFilter,
      postalCode,
      query,
      value,
    };
  }, [value, onChange, onFilter, query]);
};
